// Externals imports.
import { InputAdornment, TextField, TextFieldProps } from '@material-ui/core'
import React, { FC, MutableRefObject, ReactElement, ReactNode, useContext, useRef } from 'react'
import uuid from 'uuid-random'
import {
    Controller,
    ControllerRenderProps,
    useFormContext,
    UseFormMethods
} from '../../../../Vendor/react-hook-form/dist/index.ie11'

import Label from '../Label'
import { ReactRef, Rules } from '../../Types/Types'
import { CommonFormContext, FormContextType } from '../../Contexts/CommonFormContext'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

// CSS imports. Commented out because of current build process.
// import './textInput.scss'

/**
 * Helper text properties type.
 */
type HelperTextProps = {
    helperText?: string
    error?: boolean
}

/**
 * Component properties type.
 */
export type TextInputProps = {
    labelInputClassName?: string
    className?: string
    containerClassName?: string
    inputName?: string
    defaultValue?: string
    value?: string | number
    error?: string
    helpText?: string
    placeholder?: string
    onChange?: (newValue: string) => void
    labelInput?: string
    labelLeft?: string
    disabled?: boolean
    isLabelHidden?: boolean
    elementRef?: React.MutableRefObject<null>
    inputRef?: ReactRef
    onClick?: (event: React.MouseEvent) => void
    type?: string
    adornmentEnd?: ReactNode
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
    autoFocus?: boolean
    onBlur?: (value?: string) => void
    onEnter?: (event: React.KeyboardEvent) => void
    required?: boolean
    autoComplete?: string
    maxLength?: number
    rules?: Rules
    variant?: 'standard' | 'filled' | 'outlined'
    min?: number
    max?: number
    multiline?: boolean
    rows?: number
}

/**
 * Text input component.
 *
 * @param className Class name property.
 * @param inputName Input name property.
 * @param defaultValue Default value set for text input.
 * @param value Current value.
 * @param error Error message for this input.
 * @param helpText Help text for input.
 * @param onChange Change handler.
 * @param placeholder Placeholder text.
 * @param labelInput Label shown in the input.
 * @param labelLeft Label shown on the left side of the input.
 * @param disabled
 * @param isLabelHidden
 * @param elementRef
 * @param inputRef
 * @param onClick
 * @param type text | number etc
 * @param adornmentEnd Adornment added to the end of the input.
 * @param onKeyDown Callback on key down.
 * @param autoFocus Whether the input is focused.
 * @param onBlur Callback on blur.
 * @param minLength Minimum length of the text input.
 * @param rules Rules for the validation of the field.
 * @param variant Variant of the text field.
 * @param required Whether the field is required.
 * @param autoComplete Auto complete attribute for the input.
 * @param maxLength Maximum length of the text input.
 * @param max Maximum value for the input if type is number.
 * @param min Minimum value for the input if type is number.
 * @param multiline Whether the input is multiline.
 * @param rows Number of rows for the multiline input.
 * @return ReactElement
 */
const TextInput: FC<TextInputProps> = ({
    labelInputClassName,
    className,
    containerClassName,
    inputName,
    defaultValue,
    value,
    error,
    helpText,
    onChange,
    placeholder,
    labelInput,
    labelLeft,
    disabled,
    isLabelHidden,
    elementRef,
    inputRef,
    onClick,
    type = 'string',
    adornmentEnd,
    onKeyDown,
    autoFocus,
    onBlur,
    onEnter,
    required,
    autoComplete,
    maxLength,
    rules,
    variant,
    min,
    max,
    multiline,
    rows
}: TextInputProps): ReactElement => {
    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const id: MutableRefObject<string> = useRef(uuid())

    /**
     * Prepares helper text properties.
     */
    const getHelperTextProps = (): HelperTextProps => {
        const helperTextProps: HelperTextProps = {}

        if (error) {
            helperTextProps.helperText = error
            helperTextProps.error = true
        } else if (helpText) {
            helperTextProps.helperText = helpText
        }

        return helperTextProps
    }

    /**
     * Handles change event of the text input field.
     * @param event Change event.
     */
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (onChange) {
            onChange(event.target.value)
        }
    }

    /**
     * Handles Blur event of the text input field.
     * @param event Blur event.
     */
    const handleBlur = (event: React.FocusEvent<HTMLInputElement>): void => {
        if (onBlur) {
            onBlur(event.target.value)
        }
    }

    const labelClass: string = `label-left ${labelInputClassName} ${isLabelHidden ? 'visually-hidden' : ''}`

    const props: TextFieldProps = {
        name: inputName,
        inputRef,
        value,
        required,
        autoComplete,
        className
    }

    let methods: Nullable<UseFormMethods>

    if (inputName) {
        methods = useFormContext()
    }

    return (
        <div className={`text-input ${containerClassName}`}>
            {labelLeft && <Label customClass={labelClass}>{labelLeft}</Label>}
            <div className={`input ${labelInputClassName}`}>
                {methods && inputName && (
                    <Controller
                        control={methods.control}
                        name={inputName}
                        defaultValue={methods?.watch(inputName, '')}
                        rules={rules}
                        render={({
                            onChange: onChangeForm,
                            onBlur: onBlurForm,
                            value: valueForm,
                            name,
                            ref
                        }: ControllerRenderProps<Record<string, unknown>>): ReactElement => (
                            <TextField
                                inputProps={{ maxLength, min, max }}
                                {...props}
                                name={name}
                                inputRef={ref}
                                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                                value={valueForm}
                                label={labelInput}
                                defaultValue={defaultValue || value}
                                helperText={error}
                                placeholder={placeholder || undefined}
                                onChange={(
                                    event: React.ChangeEvent<{
                                        name?: string | undefined
                                        value: unknown
                                    }>
                                ): void => {
                                    if (commonForm?.handleFormChange) {
                                        commonForm?.handleFormChange(name)
                                    }
                                    const inputValue: string = event.target.value as string
                                    onChangeForm(inputValue)
                                    if (onChange) {
                                        onChange(inputValue)
                                    }
                                }}
                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>): void => {
                                    if (event.key === 'Enter' && onEnter) {
                                        onEnter(event)
                                    }

                                    onKeyDown?.(event)
                                }}
                                disabled={disabled}
                                onClick={onClick}
                                InputProps={{
                                    endAdornment: isVarNotEmpty(adornmentEnd) ? (
                                        <InputAdornment position='end'>{adornmentEnd}</InputAdornment>
                                    ) : undefined,
                                    style: { resize: 'vertical' }
                                }}
                                {...getHelperTextProps()}
                                ref={elementRef}
                                type={type}
                                autoFocus={autoFocus}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>): void => {
                                    onBlurForm()
                                    if (handleBlur) {
                                        handleBlur(e)
                                    }
                                }}
                                variant={variant}
                                multiline={multiline}
                                minRows={rows}
                            />
                        )}
                    />
                )}

                {(!methods || !inputName) && (
                    <TextField
                        inputProps={{ maxLength, id: id.current, min, max }}
                        {...props}
                        inputRef={inputRef}
                        value={value}
                        label={labelInput}
                        defaultValue={defaultValue}
                        helperText={error}
                        placeholder={placeholder || undefined}
                        onChange={handleChange}
                        disabled={disabled}
                        onClick={onClick}
                        InputProps={{
                            endAdornment: adornmentEnd ? (
                                <InputAdornment position='end'>{adornmentEnd}</InputAdornment>
                            ) : undefined,
                            style: { resize: 'vertical' }
                        }}
                        InputLabelProps={{ htmlFor: id.current }}
                        {...getHelperTextProps()}
                        ref={elementRef}
                        type={type}
                        onKeyDown={onKeyDown}
                        autoFocus={autoFocus}
                        onBlur={handleBlur}
                        variant={variant}
                        multiline={multiline}
                        minRows={rows}
                    />
                )}
            </div>
        </div>
    )
}

export default TextInput
